import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LuArchive,
  LuPause,
  LuRotateCcw,
  LuTrash2,
  LuClock,
  LuCheck,
  LuLoaderCircle,
} from "react-icons/lu";
import { TpvDialog } from "../../../components/TpvDialog";
import { store } from "../../../store";
import { selectParkedSale } from "../../../reducers/userConfigSlice";
import { searchCustomerInformation } from "../../../api/commander";
import * as api from "../../../api/parkedCarts";
import {
  flushAllProductChanges,
  hasAnyPendingProductChanges,
} from "../../../services/productSyncQueue";
const money = (value) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(
    value,
  );
const signature = (cart) =>
  JSON.stringify(
    (cart?.ShoppingCartProducts || [])
      .map((l) => [
        l.productId,
        Number(l.quantity),
        l.variantId || "",
        [...(l.itemIds || [])].sort(),
        l.note || "",
      ])
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
  );
const current = () => store.getState().aplicationConfig;
const age = (date) => {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(date).getTime()) / 60000),
  );
  return minutes < 1
    ? "ahora mismo"
    : minutes < 60
      ? `hace ${minutes} min`
      : new Intl.DateTimeFormat("es-ES", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(date));
};
export const ParkedCarts = () => {
  const dispatch = useDispatch();
  const app = useSelector((s) => s.aplicationConfig);
  const user = app.userConfig;
  const pointId = app.corporationData.pointConfig?.PointOfSaleConfigurationId;
  const storageKey = `tpv-park-operation:${pointId}:${user.data?.userId}`;
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [result, setResult] = useState(null),
    [page, setPage] = useState(1),
    [confirm, setConfirm] = useState(null);
  const [pending, setPending] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey));
    } catch {
      return null;
    }
  });
  const guard = useRef(false);
  const actionsRef = useRef(null);
  const [operationKind, setOperationKind] = useState(null);
  const [notice, setNotice] = useState("");
  const inlineBusy = busy && operationKind !== "discard";
  useEffect(() => {
    if (!inlineBusy) return;
    const actions = actionsRef.current;
    const workspace = actions?.closest(".tpvWorkspace");
    if (!workspace) return;
    const wasInert = workspace.hasAttribute("inert");
    workspace.setAttribute("inert", "");
    return () => {
      if (!wasInert) workspace.removeAttribute("inert");
      actions?.querySelector("button:not(:disabled)")?.focus();
    };
  }, [inlineBusy]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 2200);
    return () => clearTimeout(timer);
  }, [notice]);
  const readVersion = useRef(0);
  const refresh = useCallback(async (p = 1) => {
    const version = ++readVersion.current;
    try {
      const response = await api.listParkedCarts(p);
      if (version !== readVersion.current) return;
      if (response?.error !== false || !Array.isArray(response.data?.rows))
        throw Error(
          response?.message || "No se pudieron cargar los carritos aparcados.",
        );
      setResult(response.data);
      setPage(p);
    } catch (error) {
      if (version === readVersion.current) throw error;
    }
  }, []);
  useEffect(() => {
    refresh().catch(() => setResult(null));
    const handler = () => refresh().catch(() => {});
    window.addEventListener("focus", handler);
    window.addEventListener("tpv-parked-carts-changed", handler);
    return () => {
      window.removeEventListener("focus", handler);
      window.removeEventListener("tpv-parked-carts-changed", handler);
    };
  }, [refresh, pointId]);
  const remember = (operation) => {
    sessionStorage.setItem(storageKey, JSON.stringify(operation));
    setPending(operation);
  };
  const forget = () => {
    sessionStorage.removeItem(storageKey);
    setPending(null);
  };
  const load = async () => {
    try {
      setPending(JSON.parse(sessionStorage.getItem(storageKey)));
    } catch {
      setPending(null);
    }
    setOpen(true);
    setError("");
    try {
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  };
  const unchanged = (start) => {
    const next = current().userConfig;
    if (
      next.order?.orderId ||
      next.client?.customerId !== start.client?.customerId ||
      (next.cart?.shoppingCartId || null) !==
        (start.cart?.shoppingCartId || null) ||
      signature(next.cart) !== signature(start.cart) ||
      next.selectedShippingMethodId !== start.selectedShippingMethodId ||
      next.data?.userId !== start.data?.userId ||
      hasAnyPendingProductChanges()
    )
      throw Error(
        "La venta ha cambiado. La comanda se conserva; vuelve a intentarlo.",
      );
  };
  const prepare = async (start) => {
    const cart = start.cart;
    if (!cart?.shoppingCartId || !cart.ShoppingCartProducts?.length)
      throw Error("Añade algún producto antes de aparcar.");
    const response = await api.readParkCurrent(
      cart.shoppingCartId,
      start.client.customerId,
    );
    if (
      response?.error !== false ||
      signature(response.data) !== signature(cart)
    )
      throw Error(
        response?.message ||
          "El carrito cambió en otra pantalla. Actualiza antes de aparcar.",
      );
    return {
      requestId: crypto.randomUUID(),
      shoppingCartId: cart.shoppingCartId,
      shippingMethodId: start.selectedShippingMethodId,
      expectedUpdatedAt: response.data.updatedAt,
      expectedVersion: response.data.parkVersion,
      ...(current().corporationData.pointMethods.shipping.find(
        (m) => m.shippingMethodId === start.selectedShippingMethodId,
      )?.homeDeliveryIsActive
        ? {
            addressId:
              start.client.addresses?.shippingAddresses?.[0]?.addressId,
          }
        : {}),
    };
  };
  const perform = async (kind, row, retry = null) => {
    if (guard.current) return;
    guard.current = true;
    if (kind !== "discard") {
      setOpen(false);
      setConfirm(null);
    }
    setOperationKind(kind);
    setNotice("");
    setBusy(true);
    setError("");
    try {
      if (!(await flushAllProductChanges()))
        throw Error(
          "Hay cambios sin guardar. Revísalos antes de aparcar o recuperar.",
        );
      const start = current().userConfig;
      if (start.order?.orderId && kind !== "discard")
        throw Error(
          "Termina la vista del pedido confirmado antes de recuperar un carrito.",
        );
      let defaults = null;
      if (kind === "park") {
        const response = await searchCustomerInformation("Cliente Contado");
        const client =
          response?.error === false ? response.data?.customer?.[0] : null;
        const method = current().corporationData.pointMethods.shipping.find(
          (m) => m.storeDeliveryIsActive && !m.homeDeliveryIsActive,
        );
        if (!client?.customerId || !method)
          throw Error(
            "No se pudo preparar la siguiente venta de recogida. La comanda se conserva.",
          );
        defaults = { client, shippingMethodId: method.shippingMethodId };
      }
      let body = retry?.body || {
        expectedPointOfSaleId: pointId,
        expectedOperatorId: start.data?.userId,
        ...(kind === "park" ? await prepare(start) : {}),
        ...(kind === "recover" && start.cart?.ShoppingCartProducts?.length
          ? { current: await prepare(start) }
          : {}),
      };
      if (
        retry &&
        kind === "recover" &&
        start.cart?.ShoppingCartProducts?.length &&
        start.cart.shoppingCartId !== retry.row?.shoppingCartId &&
        start.cart.shoppingCartId !== body.current?.shoppingCartId
      )
        body = { ...body, current: await prepare(start) };
      unchanged(start);
      const operation = { kind, row, body };
      remember(operation);
      const response =
        kind === "park"
          ? await api.parkCart(body)
          : kind === "recover"
            ? await api.recoverPark(row.parkId, body)
            : await api.discardPark(row.parkId, body);
      if (response?.error !== false) {
        if (response?.data?.parkOperationRejected) forget();
        throw Error(
          response?.message ||
            "No se confirmó la operación. Reintenta: se comprobará la misma operación sin duplicarla.",
        );
      }
      if (kind !== "discard") {
        unchanged(start);
        const selection = kind === "park" ? defaults : response.data;
        if (kind === "park" && response.data.status !== "PARKED") {
          forget();
          throw Error(
            "Esta operación ya se completó. Actualiza la venta antes de continuar.",
          );
        }
        const keepCurrent =
          retry &&
          kind === "park" &&
          start.cart?.ShoppingCartProducts?.length &&
          start.cart.shoppingCartId !== body.shoppingCartId;
        if (!keepCurrent)
          dispatch(
            selectParkedSale({
              expectedCartId: start.cart?.shoppingCartId,
              expectedCustomerId: start.client?.customerId,
              ...selection,
            }),
          );
        setNotice(
          kind === "park"
            ? keepCurrent
              ? "Carrito aparcado · Comanda actual conservada"
              : "Carrito aparcado · Nueva venta lista"
            : "Carrito recuperado · Revisa el pedido",
        );
      }
      forget();
      setResult(null);
      window.dispatchEvent(new Event("tpv-parked-carts-changed"));
      setConfirm(null);
      setOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      guard.current = false;
      setBusy(false);
    }
  };
  return (
    <>
      <div className="tpvParkActions" ref={actionsRef}>
        <button
          type="button"
          className="tpvParkCount"
          disabled={busy}
          onClick={load}
        >
          <LuArchive aria-hidden="true" />
          {pending && !busy
            ? "Operación pendiente"
            : result
              ? `${result.count} aparcados`
              : "Aparcados"}
        </button>
        <button
          type="button"
          className="tpvParkButton"
          disabled={
            busy ||
            Boolean(user.order?.orderId) ||
            !user.cart?.ShoppingCartProducts?.length ||
            Boolean(pending)
          }
          onClick={() => perform("park")}
        >
          <LuPause aria-hidden="true" />
          {busy && operationKind === "park" ? "Aparcando…" : "Aparcar"}
        </button>
      </div>
      {!open && !busy && (error || pending) && (
        <div className="tpvParkInlineError" role="alert">
          {error && <p>{error}</p>}
          {pending && (
            <>
              <p>Hay una operación pendiente de confirmar.</p>
              <button
                type="button"
                onClick={() => perform(pending.kind, pending.row, pending)}
              >
                Comprobar operación
              </button>
            </>
          )}
        </div>
      )}
      {(inlineBusy || notice) &&
        createPortal(
          <>
            {inlineBusy && (
              <div className="tpvParkInteractionShield" aria-hidden="true" />
            )}
            <div
              className={`tpvParkProgress ${notice ? "is-complete" : ""}`}
              role="status"
              aria-live="polite"
            >
              {inlineBusy ? (
                <LuLoaderCircle aria-hidden="true" />
              ) : (
                <LuCheck aria-hidden="true" />
              )}
              <strong>
                {inlineBusy
                  ? operationKind === "park"
                    ? "Aparcando carrito…"
                    : "Recuperando carrito…"
                  : notice}
              </strong>
            </div>
          </>,
          document.body,
        )}
      {open && (
        <TpvDialog
          className="tpvParkDialog"
          title={`Tickets aparcados${result ? ` · ${result.count}` : ""}`}
          busy={busy}
          onClose={() => setOpen(false)}
        >
          <div className="tpvDialogBody">
            {error && (
              <p role="alert" className="tpvParkError">
                {error}
              </p>
            )}
            {busy && <p role="status">Guardando y comprobando la comanda…</p>}
            {pending && !busy && (
              <div className="tpvParkPending">
                <p>
                  Hay una operación pendiente de confirmar. Reintenta para
                  comprobar su resultado sin repetirla.
                </p>
                <button
                  type="button"
                  onClick={() => perform(pending.kind, pending.row, pending)}
                >
                  Comprobar operación
                </button>
              </div>
            )}
            {!pending && !busy && (
              <button
                type="button"
                className="tpvParkRefresh"
                onClick={() => refresh(page).catch((e) => setError(e.message))}
              >
                Actualizar
              </button>
            )}
            {!result && !busy && (
              <p>No se pudo leer la lista. Pulsa Actualizar para reintentar.</p>
            )}
            {result?.count === 0 && (
              <p className="tpvParkEmpty">
                No hay tickets aparcados en este puesto.
              </p>
            )}
            <div className="tpvParkGrid">
              {result?.rows.map((row) => (
                <article className="tpvParkCard" key={row.parkId}>
                  <div className="tpvParkMeta">
                    <time dateTime={row.createdAt}>
                      <LuClock aria-hidden="true" />
                      {age(row.createdAt)}
                    </time>
                    <span>
                      {row.snapshot.articles}{" "}
                      {row.snapshot.articles === 1 ? "artículo" : "artículos"}
                    </span>
                  </div>
                  <h3>{row.snapshot.customerName}</h3>
                  <p>
                    {row.snapshot.deliveryName} · {row.snapshot.lines.length}{" "}
                    {row.snapshot.lines.length === 1 ? "línea" : "líneas"}
                  </p>
                  <p className="tpvParkLines">
                    {row.snapshot.lines
                      .map((l) => `${l.quantity}× ${l.name}`)
                      .join(" · ")}
                  </p>
                  <strong className="tpvParkAmount">
                    {money(row.snapshot.amount)}
                  </strong>
                  <div className="tpvParkCardActions">
                    <button
                      type="button"
                      className="tpvParkRecover"
                      disabled={
                        busy || Boolean(pending) || Boolean(user.order?.orderId)
                      }
                      onClick={() =>
                        user.cart?.ShoppingCartProducts?.length
                          ? setConfirm({ kind: "recover", row })
                          : perform("recover", row)
                      }
                    >
                      <LuRotateCcw aria-hidden="true" />
                      Recuperar
                    </button>
                    <button
                      type="button"
                      className="tpvParkDelete"
                      aria-label={`Eliminar aparcado de ${row.snapshot.customerName}`}
                      disabled={busy || Boolean(pending)}
                      onClick={() => setConfirm({ kind: "discard", row })}
                    >
                      <LuTrash2 aria-hidden="true" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
            {result?.count > 20 && (
              <div className="tpvParkPagination">
                <button
                  disabled={busy || page === 1}
                  onClick={() =>
                    refresh(page - 1).catch((e) => setError(e.message))
                  }
                >
                  Anterior
                </button>
                <span>Página {page}</span>
                <button
                  disabled={busy || page * 20 >= result.count}
                  onClick={() =>
                    refresh(page + 1).catch((e) => setError(e.message))
                  }
                >
                  Siguiente
                </button>
              </div>
            )}
            {confirm && (
              <div className="tpvParkConfirm" role="alert">
                <p>
                  {confirm.kind === "discard"
                    ? "¿Eliminar este ticket de la lista de aparcados?"
                    : "Se aparcará la comanda actual y se recuperará este ticket. Sus artículos no se mezclarán."}
                </p>
                <button disabled={busy} onClick={() => setConfirm(null)}>
                  Cancelar
                </button>
                <button
                  disabled={busy}
                  onClick={() => perform(confirm.kind, confirm.row)}
                >
                  {confirm.kind === "discard"
                    ? "Eliminar aparcado"
                    : "Aparcar actual y recuperar"}
                </button>
              </div>
            )}
          </div>
        </TpvDialog>
      )}
    </>
  );
};
